import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@codes/ui-kit/components/ui/select';
import { toast } from '@codes/ui-kit/components/ui/use-toast';
import { SettingSetView } from '@lemoncloud/codes-goods-api';
import { SettingSetViewWithArrValue, useServiceSettings } from '@web/app/features/home/hooks';
import { X } from 'lucide-react';

import { useProductSettingsById, useUpdateProductSettings } from '@codes/product';
import {
    Button,
    Input,
    Label,
    SharedSpinner,
    Sheet,
    SheetClose,
    SheetContent,
    SheetFooter,
    SheetHeader,
    SheetTitle,
    Skeleton,
    Switch,
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@codes/ui-kit';

interface Props {
    open: boolean;
    onOpenChange: (val: boolean) => void;
    productId?: string;
    nested?: boolean;
}

export const ServiceSettingsDialog = ({ open, onOpenChange, productId, nested = false }: Props) => {
    const {
        data: settings,
        isFetching: isFetchingSettings,
        isRefetching: isRefetchingSettings,
        error: settingsFetchError,
        refetch: refetchSettings,
    } = useProductSettingsById(productId);

    const { mutateAsync: updateProductSettings, isPending: isUpdating } = useUpdateProductSettings();

    const {
        settingSetMap,
        scopes,
        replaceValue,
        addValue,
        removeValue,
        isChanged,
        getChangedSettings,
        isAllRequired,
        getErrorCase,
        addArrValue,
        reset,
    } = useServiceSettings(settings);

    const { t } = useTranslation();

    // Reset hook state when dialog closes
    useEffect(() => {
        if (!open) {
            reset();
        }
    }, [open, reset]);

    const handleAddingArrValue = useCallback(
        (setting: SettingSetView, index: number, value: string | number) => {
            addArrValue(setting, index, value);
        },
        [addArrValue]
    );

    const handleAddingValue = useCallback(
        (setting: SettingSetView, index: number, value: string | number) => {
            if (!value) {
                toast({ description: t('serviceSetting.error.empty') });
                return;
            }
            addValue(setting, index, value);
        },
        [addValue, t]
    );

    const renderInputByType = useCallback(
        (setting: SettingSetViewWithArrValue, index: number) => {
            switch (setting.type) {
                case 'boolean': {
                    const originalBoolean = settings?.find(s => s.name === setting.name)?.value as boolean | null;

                    return (
                        <Switch
                            checked={setting.value as boolean}
                            onCheckedChange={value => replaceValue(setting, index, value ? 1 : 0)}
                            disabled={!!setting.immutable && originalBoolean !== null}
                        />
                    );
                }

                case 'string': {
                    if (setting.isArray) {
                        const originalArray = (settings?.find(s => s.name === setting.name)?.value as string[]) || [];
                        const disabled = !!setting.immutable && originalArray?.length > 0;

                        return (
                            <div
                                className={`border rounded-md px-4 py-3 flex flex-wrap gap-3 ${
                                    disabled
                                        ? 'border-disabled-border'
                                        : 'border-text-input-border focus-within:border-point'
                                }`}
                            >
                                {((setting.value as string[]) || [])?.map((value, valueIndex) => (
                                    <div
                                        className="flex items-center gap-x-2 bg-detail-line px-2 py-1 rounded-lg"
                                        key={valueIndex}
                                    >
                                        {value}
                                        <Button
                                            className="p-1 h-auto"
                                            onClick={() => removeValue(setting, index, valueIndex)}
                                            disabled={disabled}
                                        >
                                            <X size={14} />
                                        </Button>
                                    </div>
                                ))}
                                {!disabled && (
                                    <input
                                        value={setting.arrValue}
                                        onChange={e => handleAddingArrValue(setting, index, e.target.value)}
                                        onKeyDown={e => {
                                            e.key === 'Enter' &&
                                                handleAddingValue(setting, index, e.currentTarget.value);
                                        }}
                                        placeholder="Please enter a value and then Press enter key"
                                        className="w-full outline-none"
                                    />
                                )}
                            </div>
                        );
                    }

                    if (setting.options) {
                        const originalStringOption = settings?.find(s => s.name === setting.name)?.value;

                        return (
                            <Select
                                value={typeof setting.value === 'string' ? setting.value : ''}
                                onValueChange={value => replaceValue(setting, index, value)}
                                disabled={!!setting.immutable && !!originalStringOption}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder={`Select a ${setting.name}`} />
                                </SelectTrigger>
                                <SelectContent>
                                    {setting.options.map((option, optionIndex) => (
                                        <SelectItem key={optionIndex} value={option}>
                                            {option}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        );
                    }

                    const originalString = settings?.find(s => s.name === setting.name)?.value;

                    return (
                        <div className="flex flex-col gap-y-2">
                            <Input
                                value={setting.value as string}
                                onChange={e => replaceValue(setting, index, e.target.value)}
                                disabled={!!setting.immutable && !!originalString}
                            />
                            {getErrorCase(setting) && (
                                <div className="text-error">{t(`serviceSetting.error.${getErrorCase(setting)}`)}</div>
                            )}
                        </div>
                    );
                }
                case 'float':
                case 'number': {
                    if (setting.isArray) {
                        const originalIntArray = (settings?.find(s => s.name === setting.name)?.value ||
                            []) as number[];
                        const disabled = !!setting.immutable && originalIntArray?.length > 0;

                        return (
                            <div className="border rounded-md px-4 py-3 flex flex-wrap gap-3">
                                {((setting.value as string[]) || [])?.map((value, valueIndex) => (
                                    <div
                                        className={`border rounded-md px-4 py-3 flex flex-wrap gap-3 ${
                                            disabled
                                                ? 'border-disabled-border'
                                                : 'border-text-input-border focus-within:border-point'
                                        }`}
                                        key={valueIndex}
                                    >
                                        {value}
                                        <Button
                                            className="p-1 h-auto"
                                            onClick={() => removeValue(setting, index, valueIndex)}
                                            disabled={disabled}
                                        >
                                            <X size={14} />
                                        </Button>
                                    </div>
                                ))}
                                {!disabled && (
                                    <input
                                        value={setting.arrValue}
                                        onChange={e => handleAddingArrValue(setting, index, e.target.value)}
                                        onKeyDown={e => {
                                            e.key === 'Enter' &&
                                                handleAddingValue(setting, index, e.currentTarget.value);
                                        }}
                                        placeholder="Please enter a value and then Press enter key"
                                        className="w-full"
                                        type="number"
                                    />
                                )}
                            </div>
                        );
                    }

                    const originalInt = settings?.find(s => s.name === setting.name)?.value as number | null;

                    return (
                        <Input
                            type="number"
                            value={setting.value as number}
                            onChange={e => replaceValue(setting, index, e.target.value)}
                            disabled={!!setting.immutable && !!originalInt}
                        />
                    );
                }

                default:
                    return <div>{t('serviceSetting.unsupportedFormat')}</div>;
            }
        },
        [settings, t, replaceValue, removeValue, getErrorCase, handleAddingArrValue, handleAddingValue]
    );

    const handleSubmit = async () => {
        const isRequired = isAllRequired();

        if (!isRequired) {
            toast({ description: t('serviceSetting.toastMessage.required') });
            return;
        }

        const setting$$ = getChangedSettings();

        if (setting$$.length === 0) {
            toast({ description: t('serviceSetting.toastMessage.noChanges') });
            return;
        }

        await updateProductSettings({
            productId: productId,
            body: {
                setting$$,
            },
        });

        onOpenChange(false);
        toast({ description: t('serviceSetting.toastMessage.updateSuccess') });
    };

    const ContentView = useMemo(() => {
        if (isFetchingSettings || isRefetchingSettings) {
            return (
                <div className="flex flex-1 flex-col gap-y-5 px-4 py-3 min-h-96">
                    <Skeleton className="h-14" />
                    <div className="flex flex-col gap-y-2">
                        <Skeleton className="h-20" />
                        <Skeleton className="h-20" />
                        <Skeleton className="h-20" />
                    </div>
                </div>
            );
        }

        if (settingsFetchError) {
            return (
                <div className="flex flex-1 items-center justify-center flex-col gap-y-2 min-h-96">
                    <div className="font-semibold text-lg">{t('serviceSetting.error.fetchData')}</div>
                    <Button disabled={isRefetchingSettings} onClick={() => refetchSettings()}>
                        {t('serviceSetting.error.retry')}
                    </Button>
                </div>
            );
        }

        if (!settings || settings?.length === 0 || scopes.length === 0) {
            return <div className="min-h-96 flex items-center justify-center">{t('serviceSetting.empty.message')}</div>;
        }

        // TODO: 따로 컴포넌트 파는게 나을듯

        const defaultScope = scopes[0];

        return (
            <div className="flex flex-col gap-y-8 divide-y overflow-auto">
                <Tabs defaultValue={defaultScope}>
                    <div className="sticky top-0 z-30 bg-dialog">
                        <TabsList className="justify-start">
                            {scopes.map((scope, scopeIndex) => (
                                <TabsTrigger value={scope} key={scopeIndex} variant="underline" className="capitalize">
                                    {scope}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </div>

                    {scopes.map((scope, scopeIndex) => (
                        <TabsContent value={scope} key={scopeIndex}>
                            <div className="pt-[30px] pb-8" key={scopeIndex}>
                                <div className="flex flex-col gap-y-9">
                                    {settingSetMap[scope]
                                        ?.filter(setting => setting.scope === scope)
                                        .map((setting, settingIndex) =>
                                            setting.isHidden ? null : (
                                                <div key={settingIndex}>
                                                    <Label variant="form" className="flex flex-col space-y-1 text-lg">
                                                        <div>
                                                            <span className="capitalize">{setting.name}</span>
                                                            {setting.isRequired && (
                                                                <span className="text-error">*</span>
                                                            )}
                                                        </div>
                                                        <div className="text-sm text-sub">{setting.description}</div>
                                                    </Label>
                                                    {renderInputByType(setting, settingIndex)}
                                                </div>
                                            )
                                        )}
                                </div>
                            </div>
                        </TabsContent>
                    ))}
                </Tabs>
            </div>
        );
    }, [
        isFetchingSettings,
        isRefetchingSettings,
        settingsFetchError,
        settings,
        scopes,
        settingSetMap,
        renderInputByType,
        t,
        refetchSettings,
    ]);

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="overflow-y-scroll" nested={nested} preventDismiss={false}>
                <SheetHeader className="justify-between w-full flex" aria-describedby={undefined}>
                    <SheetTitle>Settings</SheetTitle>
                    <SheetClose>
                        <X className="w-5 h-5" />
                    </SheetClose>
                </SheetHeader>
                {ContentView}
                <SheetFooter className="px-6">
                    {isChanged && (
                        <Button size="lg" className="w-full mb-6" disabled={isUpdating} onClick={handleSubmit}>
                            {isUpdating ? <SharedSpinner /> : t('serviceSetting.save')}
                        </Button>
                    )}
                </SheetFooter>
            </SheetContent>
        </Sheet>
    );
};
